// ---------------------------------------------------------------------------
// The router — the ONLY module in the app that calls pushState/replaceState.
// ---------------------------------------------------------------------------
// Before this, the app had no route model: the sole URL sync was the open task via
// replaceState, so Back did nothing, refresh always dumped you on the Dashboard,
// and "the board for project X" was unlinkable.
//
// THREE RULES make URL⇄store ping-pong structurally impossible:
//
//  1. Only this module writes history, and never via `location.hash = …` (that
//     fires an async hashchange no synchronous guard can cover).
//  2. `pushState` comes ONLY from a declared user intent (nav.mark / nav.tx). The
//     store→URL reconciler is REPLACE-ONLY, so a correction after a refused or
//     altered apply costs one silent rewrite, never a corrupted back-stack.
//  3. Both directions are idempotent by STRING COMPARISON, not a boolean. There is
//     deliberately no `applying` flag: it would clear before the effect it is
//     meant to suppress ever ran (which is exactly why the old code lost deep
//     links on mount).
//
// The writer is a zustand SUBSCRIPTION, not a useEffect. Two sibling effects
// cannot be a reader/writer pair: on the first commit both run in the same passive
// phase, so the writer sees render-0 state (selectedAreaId: null) and replaceStates
// `#/` over the incoming deep link. `set()` notifies subscribers synchronously, so
// a subscription always reads post-mutation truth.
// ---------------------------------------------------------------------------

import { useAreaStore } from '@/features/areas/store/areaStore';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { nav } from './navIntent';
import { parse, serialize, type Route } from './route';

let started = false;
let bootDone = false; // history seeded; NOT reset by unmount (React StrictMode)
let lastKey = '';     // the hash this router last consumed or wrote
let seq = 0;
/** AreaId of a viewless `#/p/<id>` whose view resolution is parked until /areas
 *  answers. Resolving earlier reads an empty areas array, so the project's team
 *  default (Area.defaultView) is invisible — and the reconciler would then
 *  canonicalize '/board' into the hash, losing it for good. */
let pendingView: number | null = null;

const href = (h: string) => `${window.location.pathname}${window.location.search}${h}`;

function currentRoute(): Route {
  const a = useAreaStore.getState();
  const t = useTaskDetailStore.getState();
  return {
    areaId: a.selectedAreaId,
    view: a.selectedAreaId === null ? null : a.view,
    taskId: t.openId,
  };
}

/** Write one history entry. Besides the hash, each entry of ours records `under`:
 *  the hash of the entry DIRECTLY BENEATH it at the moment it was pushed. History
 *  gives no way to read a neighbouring entry's URL, and dismissTask() needs to
 *  know what one Back would land on to choose between consuming the entry and
 *  replacing in place (see the close matrix there). A push covers whatever hash
 *  is current, so that IS its `under`; a replace changes nothing beneath, so it
 *  carries the existing entry's `under` forward (undefined when the entry wasn't
 *  ours — a browser-pushed pasted hash has no floor we can vouch for).
 *
 *  `coldSeed` (seedHistory only): the boot sequence pushes the deep link over a
 *  Home entry IT just manufactured. That Home is an artificial floor the user
 *  never stood on — recording it as `under` would make ✕ on a fresh-tab deep
 *  link back() onto a dashboard they never came from. Left undefined, the close
 *  matrix treats the entry as a cold open and replaces instead. */
function write(mode: 'push' | 'replace', hash: string, coldSeed = false): void {
  const prev = window.history.state as { cgp?: number; under?: string } | null;
  const under = mode === 'push'
    ? (coldSeed ? undefined : window.location.hash)
    : (prev?.cgp ? prev.under : undefined);
  const state = { cgp: 1, seq: ++seq, route: hash, under };
  if (mode === 'push') window.history.pushState(state, '', href(hash));
  else window.history.replaceState(state, '', href(hash));
  lastKey = hash;
}

/** store → URL. Runs after EVERY store mutation. Compare, then write. */
function reconcile(): void {
  if (!bootDone || nav.suspended()) return;
  if (pendingView !== null) {
    const a = useAreaStore.getState();
    const id = pendingView;
    if (a.selectedAreaId !== id) {
      pendingView = null; // route moved on before /areas answered — nothing to finish
    } else if (!a.areasLoaded) {
      return; // hash stays the verbatim '#/p/<id>' — no write until we can resolve
    } else {
      // areasLoaded's own set() lands here: finish the deferred resolution now
      // that the team default is knowable. Cleared BEFORE the tx so the flush
      // re-enters on the normal path and writes the canonical hash (replace —
      // this is a correction, not a navigation).
      pendingView = null;
      nav.tx('replace', () => { a.setViewFromRoute(a.applyPrefs(id)); });
      return;
    }
  }
  const want = serialize(currentRoute());
  if (want === window.location.hash) { nav.reset(); return; } // faithful — nothing to do
  write(nav.mode, want);
  nav.reset();
}
nav._bind(reconcile);

/** Apply a route to the stores. Diff-then-act, fully synchronous, never awaits.
 *
 *  Diffing matters: selectArea/open are NOT idempotent (one resets filters and
 *  re-reads localStorage prefs, the other can pop a discard confirm), so
 *  re-asserting a route that is already satisfied would wipe filters or drafts. */
function applyRoute(r: Route): void {
  const a = useAreaStore.getState();
  // An explicit URL view for the parked project ends the deferral: the segment
  // outranks anything /areas could resolve, and a park left armed here would
  // overwrite it the moment areasLoaded flips.
  if (pendingView !== null && r.areaId === pendingView && r.view !== null) pendingView = null;
  if (r.areaId !== a.selectedAreaId) {
    if (r.areaId === null) a.goHomeFromRoute();
    else {
      // Viewless + pre-areas: the select below still resolves a PROVISIONAL view
      // (nothing renders it — the wait screen holds until areasLoaded), but the
      // hash write is parked so the real resolution can see the team default.
      pendingView = r.view === null && !a.areasLoaded ? r.areaId : null;
      a.selectAreaFromRoute(r.areaId, r.view ?? undefined); // URL view WINS over prefs
    }
  } else if (r.view && r.view !== a.view) {
    a.setViewFromRoute(r.view);
  }
  const t = useTaskDetailStore.getState();
  if (r.taskId !== t.openId) {
    if (r.taskId === null) t.closeFromRoute();
    else void t.openFromRoute(r.taskId); // void: openId is set before the first await
  }
}

/** URL → store. One handler on BOTH popstate and hashchange, deduped by value.
 *  popstate alone misses hand-edited address bars and pasted links; hashchange
 *  alone misses same-hash traversals. Never reason about which fired — the
 *  string key is the idempotency token. */
function onHistoryEvent(): void {
  const { route, canonical } = parse(window.location.hash);
  const want = serialize(route);
  // Snapshot BEFORE canonicalizing: write() stamps `lastKey` with what it wrote
  // (correct for the reconciler claiming its own outbound writes), so reading
  // lastKey after the rewrite below would compare `want` against itself and
  // return every single time — silently dropping the apply for every legacy
  // `#t/<id>` and every hand-typed hash.
  const consumed = lastKey;

  // Canonicalize BEFORE applying when the canonical form is purely computable
  // (legacy #t/, junk, a bad view). `#/p/12` (no view) resolves after the apply,
  // via reconcile, once the remembered view is known.
  if (!canonical && route.view !== null) write('replace', want);
  else if (!canonical && route.areaId === null) write('replace', want);

  if (want === consumed) return; // the popstate+hashchange pair, or a no-op traversal
  lastKey = want;

  // tx does three jobs: suspends the writer during a multi-store apply (one write,
  // not three), forces the flush to be a REPLACE (so a non-round-tripping URL can
  // never create a Back trap), and neutralizes the nav.mark('push') that
  // selectArea/open declare internally.
  nav.tx('replace', () => { applyRoute(route); });
}

function seedHistory(): void {
  const { route } = parse(window.location.hash);
  const want = serialize(route);
  // `#/p/12` with no view is the one form whose canonical shape is NOT computable
  // here — it depends on the view this person last used in that project, or failing
  // that on the team default /areas hasn't delivered yet. Seed it verbatim so the
  // apply can defer; the reconciler writes the resolved hash (for a cold boot,
  // only once /areas answers). Pre-canonicalizing it would silently force 'board'
  // and override the remembered view on every cold deep link.
  const seed = route.areaId !== null && route.view === null ? window.location.hash : want;
  write('replace', '#/');                  // Home always exists beneath
  // coldSeed: the Home floor above is manufactured, not a place the user came
  // from — the entry must NOT record it as `under`, so ✕ on a cold-booted deep
  // link replaces to the task's own project (close matrix case C) instead of
  // back()ing onto a dashboard they never saw.
  if (want !== '#/') write('push', seed, true); // ⇒ Back from a deep link stays in the app
  lastKey = '';                            // force the first apply
  onHistoryEvent();
  // The apply is done, so the store is now the truth: open the writer and let it
  // replace the seeded entry with the canonical hash (deferred view included).
  bootDone = true;
  reconcile();
}

/** Mount once from the authenticated shell. Returns a cleanup. */
export function startRouter(): () => void {
  if (!bootDone) seedHistory(); // once per sign-in, ever; sets bootDone itself
  if (started) return () => {};
  started = true;
  const unsubs = [
    useAreaStore.subscribe(reconcile),
    useTaskDetailStore.subscribe(reconcile),
  ];
  window.addEventListener('popstate', onHistoryEvent);
  window.addEventListener('hashchange', onHistoryEvent);
  return () => {
    // bootDone deliberately survives: StrictMode remounts and must NOT re-seed.
    started = false;
    unsubs.forEach((u) => u());
    window.removeEventListener('popstate', onHistoryEvent);
    window.removeEventListener('hashchange', onHistoryEvent);
  };
}

/** Sign-out: drop the previous user's route so the next person on a shared tablet
 *  doesn't get auto-navigated into their project and task. */
export function resetRouter(): void {
  bootDone = false;
  started = false;
  lastKey = '';
  seq = 0;
  pendingView = null;
  try { window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`); } catch { /* ignore */ }
}

/** Close the open task panel in ONE step — never by pushing a new entry (Back
 *  would re-open the task just dismissed), and never by walking history more
 *  than once (in-panel task-to-task travel is REPLACE, so the panel holds one
 *  entry no matter how far the user wandered — Alexis's ✕-acts-like-Back bug).
 *  Call AFTER any discard confirm has been accepted.
 *
 *  THE CLOSE MATRIX — chosen by what the entry beneath (`under`, stamped by
 *  write()) actually is, compared to the CURRENT task's project:
 *
 *  A. `under` known and it is this task's own project view, Home, or another
 *     task entry → history.back(): consume the panel's entry. Landing is exactly
 *     where the user came from, Forward re-opens the task, and ✕ ≡ browser Back.
 *     (Home beneath = the task was opened from a Home surface — inbox, My Work,
 *     search — so "where you came from" IS Home. A task entry beneath = a
 *     search/report/toast door was used while a task was already open; each door
 *     is its own push, so ✕ steps back to the prior task, once.)
 *  B. `under` known but it is a DIFFERENT project's view than the current
 *     task's (cross-project travel via a blocked-by ref) → replace in place to
 *     the current task's project view: the task the user is leaving lives THERE,
 *     not on the board they started from. back() would still be one step, but it
 *     would land somewhere the closed task isn't. The original board stays one
 *     further Back beneath.
 *
 *     EXCEPT when the card MOVED while the panel was open (`movedWhileOpen`):
 *     then B is wrong and A is right. Approving at the last step of Short Form
 *     Content Team graduates the card to Content Calendar, and following it
 *     dumped the approver onto a board they never chose — Allyson, 2026-09-23:
 *     "once you close that project, you end up in 'content calendar', instead of
 *     staying in 'short form projects'". The user's own position outranks the
 *     card's: they are still standing on the board they opened it from, so close
 *     puts them back there and the card is one click away on its new home. Travel
 *     INSIDE the panel re-stamps openedAreaId (it re-enters fetchInto), so a
 *     blocked-by hop across projects is untouched by this.
 *  C. `under` unknown → cold open (fresh-tab deep link over the manufactured
 *     Home floor, a pasted/hand-edited hash whose entry the browser minted, or
 *     an entry stamped before `under` existed) → NEVER back(): what one Back
 *     reaches is unknown or artificial, and must not dump the user out of the
 *     app or onto a floor they never stood on. Replace in place to the task's
 *     own project view.
 *
 *  In B and C, landing on the task's project requires actually being able to
 *  open it: a task-level-access assignee (v1.14.1) is not a member, and a board
 *  they can't load would be a dead end. When the project isn't in this account's
 *  areas (or /areas hasn't answered yet), B degrades to back() — the in-app
 *  entry beneath is a fine place to stand — and C degrades to closing over the
 *  current route (Home for a bare `#/t/<id>`), exactly the pre-matrix fallback.
 *
 *  Deliberate consequence (documented, not dodged): browser Back while the panel
 *  is open is the SAME single step as case A — in-panel retracing via Back is
 *  gone; the panel's own parent / prev-next / sub-task links are the retrace. */
export function dismissTask(): void {
  const st = window.history.state as { cgp?: number; under?: string } | null;
  const under = st?.cgp ? st.under : undefined;
  const t = useTaskDetailStore.getState();
  // Unknown while the fetch is still in flight or failed — the matrix treats
  // "can't name the task's project" as "can't land on it".
  const taskArea = t.task?.areaId;
  const a = useAreaStore.getState();
  const canLand = taskArea !== undefined && a.areas.some((x) => x.id === taskArea);
  // The card left the board the user is standing on WHILE they were looking at
  // it (graduation on approval, a teammate's move, "Move to project…"). See the
  // B-exception above: close stays put rather than chasing it.
  const movedWhileOpen = taskArea !== undefined && t.openedAreaId !== null && t.openedAreaId !== taskArea;

  if (under !== undefined) {
    const behind = parse(under).route;
    const cross = behind.taskId === null && behind.areaId !== null
      && taskArea !== undefined && behind.areaId !== taskArea;
    if (!cross || !canLand || movedWhileOpen) { window.history.back(); return; } // case A (B degrades here too)
  }

  // Case B (cross-project) / case C (cold open): one REPLACE, no traversal.
  // tx collapses close + the possible project switch into a single write, and
  // forces it to be a replace even though selectAreaFromRoute is involved.
  nav.tx('replace', () => {
    t.close();
    if (canLand && taskArea !== undefined && useAreaStore.getState().selectedAreaId !== taskArea) {
      // undefined view = this person's remembered view for that project (or the
      // team default) — same resolution a viewless deep link gets.
      useAreaStore.getState().selectAreaFromRoute(taskArea, undefined);
    }
  });
  // Belt for the flush having been skipped (never expected post-boot): the URL
  // must not keep naming a task the store no longer has open.
  const want = serialize(currentRoute());
  if (window.location.hash !== want) write('replace', want);
}
